import { forwardRef, useImperativeHandle, useRef } from "react";

/**
 * HowWeWorkCards — top-most card layer for the "How We Work" section.
 *
 * ────────────────────────────────────────────────────────────────────
 *  ⚠️ EDITING GUIDE ⚠️
 *
 *  Each of the four cards below is written as an INDEPENDENT JSX block
 *  (see "CARD 01" / "CARD 02" / "CARD 03" / "CARD 04" markers).
 *
 *  → Want to change a card's IMAGE?  edit that card's <img src="..." />
 *  → Want to change a card's TITLE / BLURB / LABEL?  edit the text inside
 *    that card's JSX block only. No effect on the other three.
 *
 *  Animation params (position, entry/exit timing, rotation) live in
 *  CARD_MOTION[0..3] — those are consumed by HowWeWork's rAF loop.
 * ────────────────────────────────────────────────────────────────────
 *
 *  Scene sequencing (matches CARD_MOTION):
 *
 *   Scene A (progress 0.00 → 0.60)
 *     · Card 01 (Mindfulness) — UPPER-LEFT   enters first
 *     · Card 02 (CBT)         — MIDDLE-RIGHT enters second
 *     · Both drift UP + fade OUT together at the end of scene A
 *
 *   Scene B (progress 0.62 → 0.96)
 *     · Card 03 (Art)         — MIDDLE-LEFT  enters third
 *     · Card 04 (ACT)         — UPPER-RIGHT  enters fourth
 * ────────────────────────────────────────────────────────────────────
 */

export interface CardMotion {
  /** Final resting position (percent-based, inside the sticky viewport). */
  finalLeft: string;
  finalTop: string;
  /** Entry offset — where the card comes IN from (vw / vh). */
  fromX: number;
  fromY: number;
  /** Rotation while entering, and final resting rotation. */
  fromRot: number;
  toRot: number;
  /** Enter progress window. */
  enterStart: number;
  enterEnd: number;
  /** Optional exit window — fades out + drifts up. */
  exitStart?: number;
  exitEnd?: number;
  /** Extra Y offset (vh) applied while exiting. Negative = upward. */
  exitDy?: number;
}

// Animation-only config. Card 0 = 01, Card 1 = 02, Card 2 = 03, Card 3 = 04.
export const CARD_MOTION: CardMotion[] = [
  /* 01 Mindfulness */ {
    finalLeft: "6%",
    finalTop: "14%",
    fromX: -32,
    fromY: -6,
    fromRot: -18,
    toRot: -6,
    enterStart: 0.0,
    enterEnd: 0.14,
    exitStart: 0.42,
    exitEnd: 0.58,
    exitDy: -60,
  },
  /* 02 CBT */ {
    finalLeft: "54%",
    finalTop: "26%",
    fromX: 36,
    fromY: 4,
    fromRot: 22,
    toRot: 5,
    enterStart: 0.2,
    enterEnd: 0.34,
    exitStart: 0.44,
    exitEnd: 0.6,
    exitDy: -60,
  },
  /* 03 Art */ {
    finalLeft: "8%",
    finalTop: "28%",
    fromX: -18,
    fromY: 34,
    fromRot: -20,
    toRot: -5,
    enterStart: 0.62,
    enterEnd: 0.76,
  },
  /* 04 ACT */ {
    finalLeft: "56%",
    finalTop: "12%",
    fromX: 20,
    fromY: 32,
    fromRot: 20,
    toRot: 7,
    enterStart: 0.82,
    enterEnd: 0.96,
  },
];

// Legacy alias so HowWeWork.tsx keeps working without changes.
export const CARDS = CARD_MOTION;
export type CardDef = CardMotion;

export interface HowWeWorkCardsHandle {
  getCards: () => Array<HTMLDivElement | null>;
  getConfig: () => CardMotion[];
}

// Shared visual constants ── keep the four cards consistent
const CARD_WIDTH = "clamp(320px, 30vw, 440px)";
const CARD_SHADOW =
  "0 1px 2px oklch(var(--foreground-950) / 0.06), 0 12px 32px oklch(var(--foreground-950) / 0.12)";
const OVERLAY_GRADIENT =
  "linear-gradient(to top, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.42) 40%, rgba(0,0,0,0) 100%)";

const HowWeWorkCards = forwardRef<HowWeWorkCardsHandle>((_props, ref) => {
  const cardRefs = useRef<Array<HTMLDivElement | null>>([]);

  useImperativeHandle(
    ref,
    () => ({
      getCards: () => cardRefs.current,
      getConfig: () => CARD_MOTION,
    }),
    [],
  );

  return (
    <div className="absolute inset-0 z-40 pointer-events-none">
      <div className="relative w-full h-full max-w-[1400px] mx-auto">
        {/* ═══════════════════════════════════════════════════════════════
            CARD 01 · Mindfulness-Based Therapy
            → edit image / title / blurb / label freely below
        ═══════════════════════════════════════════════════════════════ */}
        <div
          ref={(el) => {
            cardRefs.current[0] = el;
          }}
          className="absolute will-change-transform"
          style={{
            left: CARD_MOTION[0].finalLeft,
            top: CARD_MOTION[0].finalTop,
            width: CARD_WIDTH,
            transform: `translate3d(${CARD_MOTION[0].fromX}vw, ${CARD_MOTION[0].fromY}vh, 0) rotate(${CARD_MOTION[0].fromRot}deg)`,
            opacity: 0,
            transformOrigin: "50% 50%",
          }}
        >
          <article
            className="relative overflow-hidden rounded-2xl"
            style={{ aspectRatio: "3 / 4", boxShadow: CARD_SHADOW }}
          >
            {/* IMAGE — CARD 01 */}
            <img
              src="https://static.readdy.ai/image/ef1aae41220ad17a1705abffff22a58b/877ebc466b20ec82ff198f09c7183efa.png"
              alt="Step 01 — Reach out and start the conversation"
              className="absolute inset-0 w-full h-full object-cover object-center select-none"
              loading="lazy"
              decoding="async"
              draggable={false}
            />

            {/* Bottom-up dark gradient overlay */}
            <div
              aria-hidden="true"
              className="absolute inset-x-0 bottom-0"
              style={{ height: "62%", background: OVERLAY_GRADIENT }}
            />

            {/* Top-left index label — CARD 01 */}
            <div className="absolute top-4 left-5 right-5 flex items-center justify-between">
              <span
                className="uppercase font-medium"
                style={{
                  fontSize: "10px",
                  letterSpacing: "0.3em",
                  color: "rgba(255,255,255,0.7)",
                  textShadow: "0 1px 2px rgba(0,0,0,0.25)",
                }}
              >
                STEP · 01
              </span>
              <span
                className="inline-block h-[1px] w-8"
                style={{ background: "rgba(255,255,255,0.55)" }}
              ></span>
            </div>

            {/* Bottom copy — CARD 01 */}
            <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
              <h3
                className="mb-2.5"
                style={{
                  fontFamily: "var(--font-heading)",
                  fontSize: "clamp(1.15rem, 1.4vw, 1.45rem)",
                  lineHeight: 1.2,
                  fontWeight: 500,
                  letterSpacing: "-0.01em",
                  color: "#FFFFFF",
                }}
              >
                Reach Out
              </h3>
              <p
                className="italic mb-4"
                style={{
                  fontFamily: "var(--font-heading)",
                  fontSize: "clamp(0.88rem, 1vw, 1rem)",
                  lineHeight: 1.5,
                  fontWeight: 300,
                  color: "rgba(255,255,255,0.75)",
                }}
              >
                Send a message. We&rsquo;ll listen before anything else.
              </p>
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-500"></span>
                <span
                  className="uppercase font-medium"
                  style={{
                    fontSize: "10px",
                    letterSpacing: "0.28em",
                    color: "rgba(255,255,255,0.7)",
                  }}
                >
                  The first hello
                </span>
              </div>
            </div>
          </article>
        </div>

        {/* ═══════════════════════════════════════════════════════════════
            CARD 02 · Cognitive Behavioural Therapy
            → edit image / title / blurb / label freely below
        ═══════════════════════════════════════════════════════════════ */}
        <div
          ref={(el) => {
            cardRefs.current[1] = el;
          }}
          className="absolute will-change-transform"
          style={{
            left: CARD_MOTION[1].finalLeft,
            top: CARD_MOTION[1].finalTop,
            width: CARD_WIDTH,
            transform: `translate3d(${CARD_MOTION[1].fromX}vw, ${CARD_MOTION[1].fromY}vh, 0) rotate(${CARD_MOTION[1].fromRot}deg)`,
            opacity: 0,
            transformOrigin: "50% 50%",
          }}
        >
          <article
            className="relative overflow-hidden rounded-2xl"
            style={{ aspectRatio: "3 / 4", boxShadow: CARD_SHADOW }}
          >
            {/* IMAGE — CARD 02 */}
            <img
              src="https://static.readdy.ai/image/ef1aae41220ad17a1705abffff22a58b/c83045a033bfd75cc831e8474109e248.png"
              alt="Step 02 — First session, meeting each other with care"
              className="absolute inset-0 w-full h-full object-cover object-center select-none"
              loading="lazy"
              decoding="async"
              draggable={false}
            />

            <div
              aria-hidden="true"
              className="absolute inset-x-0 bottom-0"
              style={{ height: "62%", background: OVERLAY_GRADIENT }}
            />

            {/* Top-left index label — CARD 02 */}
            <div className="absolute top-4 left-5 right-5 flex items-center justify-between">
              <span
                className="uppercase font-medium"
                style={{
                  fontSize: "10px",
                  letterSpacing: "0.3em",
                  color: "rgba(255,255,255,0.7)",
                  textShadow: "0 1px 2px rgba(0,0,0,0.25)",
                }}
              >
                STEP · 02
              </span>
              <span
                className="inline-block h-[1px] w-8"
                style={{ background: "rgba(255,255,255,0.55)" }}
              ></span>
            </div>

            {/* Bottom copy — CARD 02 */}
            <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
              <h3
                className="mb-2.5"
                style={{
                  fontFamily: "var(--font-heading)",
                  fontSize: "clamp(1.15rem, 1.4vw, 1.45rem)",
                  lineHeight: 1.2,
                  fontWeight: 500,
                  letterSpacing: "-0.01em",
                  color: "#FFFFFF",
                }}
              >
                First Session
              </h3>
              <p
                className="italic mb-4"
                style={{
                  fontFamily: "var(--font-heading)",
                  fontSize: "clamp(0.88rem, 1vw, 1rem)",
                  lineHeight: 1.5,
                  fontWeight: 300,
                  color: "rgba(255,255,255,0.75)",
                }}
              >
                A gentle space to share what brings you here.
              </p>
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-500"></span>
                <span
                  className="uppercase font-medium"
                  style={{
                    fontSize: "10px",
                    letterSpacing: "0.28em",
                    color: "rgba(255,255,255,0.7)",
                  }}
                >
                  Meeting each other
                </span>
              </div>
            </div>
          </article>
        </div>

        {/* ═══════════════════════════════════════════════════════════════
            CARD 03 · Art & Expressive Therapy
            → edit image / title / blurb / label freely below
        ═══════════════════════════════════════════════════════════════ */}
        <div
          ref={(el) => {
            cardRefs.current[2] = el;
          }}
          className="absolute will-change-transform"
          style={{
            left: CARD_MOTION[2].finalLeft,
            top: CARD_MOTION[2].finalTop,
            width: CARD_WIDTH,
            transform: `translate3d(${CARD_MOTION[2].fromX}vw, ${CARD_MOTION[2].fromY}vh, 0) rotate(${CARD_MOTION[2].fromRot}deg)`,
            opacity: 0,
            transformOrigin: "50% 50%",
          }}
        >
          <article
            className="relative overflow-hidden rounded-2xl"
            style={{ aspectRatio: "3 / 4", boxShadow: CARD_SHADOW }}
          >
            {/* IMAGE — CARD 03 */}
            <img
              src="https://static.readdy.ai/image/ef1aae41220ad17a1705abffff22a58b/755a71da6c1e837eb5c0ec42de7e8451.png"
              alt="Step 03 — Ongoing sessions, walking together over time"
              className="absolute inset-0 w-full h-full object-cover object-center select-none"
              loading="lazy"
              decoding="async"
              draggable={false}
            />

            <div
              aria-hidden="true"
              className="absolute inset-x-0 bottom-0"
              style={{ height: "62%", background: OVERLAY_GRADIENT }}
            />

            {/* Top-left index label — CARD 03 */}
            <div className="absolute top-4 left-5 right-5 flex items-center justify-between">
              <span
                className="uppercase font-medium"
                style={{
                  fontSize: "10px",
                  letterSpacing: "0.3em",
                  color: "rgba(255,255,255,0.7)",
                  textShadow: "0 1px 2px rgba(0,0,0,0.25)",
                }}
              >
                STEP · 03
              </span>
              <span
                className="inline-block h-[1px] w-8"
                style={{ background: "rgba(255,255,255,0.55)" }}
              ></span>
            </div>

            {/* Bottom copy — CARD 03 */}
            <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
              <h3
                className="mb-2.5"
                style={{
                  fontFamily: "var(--font-heading)",
                  fontSize: "clamp(1.15rem, 1.4vw, 1.45rem)",
                  lineHeight: 1.2,
                  fontWeight: 500,
                  letterSpacing: "-0.01em",
                  color: "#FFFFFF",
                }}
              >
                Ongoing Sessions
              </h3>
              <p
                className="italic mb-4"
                style={{
                  fontFamily: "var(--font-heading)",
                  fontSize: "clamp(0.88rem, 1vw, 1rem)",
                  lineHeight: 1.5,
                  fontWeight: 300,
                  color: "rgba(255,255,255,0.75)",
                }}
              >
                Weekly rhythm, gentle depth, patient practice.
              </p>
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-500"></span>
                <span
                  className="uppercase font-medium"
                  style={{
                    fontSize: "10px",
                    letterSpacing: "0.28em",
                    color: "rgba(255,255,255,0.7)",
                  }}
                >
                  Walking together
                </span>
              </div>
            </div>
          </article>
        </div>

        {/* ═══════════════════════════════════════════════════════════════
            CARD 04 · Acceptance & Commitment Therapy
            → edit image / title / blurb / label freely below
        ═══════════════════════════════════════════════════════════════ */}
        <div
          ref={(el) => {
            cardRefs.current[3] = el;
          }}
          className="absolute will-change-transform"
          style={{
            left: CARD_MOTION[3].finalLeft,
            top: CARD_MOTION[3].finalTop,
            width: CARD_WIDTH,
            transform: `translate3d(${CARD_MOTION[3].fromX}vw, ${CARD_MOTION[3].fromY}vh, 0) rotate(${CARD_MOTION[3].fromRot}deg)`,
            opacity: 0,
            transformOrigin: "50% 50%",
          }}
        >
          <article
            className="relative overflow-hidden rounded-2xl"
            style={{ aspectRatio: "3 / 4", boxShadow: CARD_SHADOW }}
          >
            {/* IMAGE — CARD 04 */}
            <img
              src="https://static.readdy.ai/image/ef1aae41220ad17a1705abffff22a58b/723703cd75986512c788d1b36641ee80.png"
              alt="Step 04 — Growth, integration and moving forward"
              className="absolute inset-0 w-full h-full object-cover object-center select-none"
              loading="lazy"
              decoding="async"
              draggable={false}
            />

            <div
              aria-hidden="true"
              className="absolute inset-x-0 bottom-0"
              style={{ height: "62%", background: OVERLAY_GRADIENT }}
            />

            {/* Top-left index label — CARD 04 */}
            <div className="absolute top-4 left-5 right-5 flex items-center justify-between">
              <span
                className="uppercase font-medium"
                style={{
                  fontSize: "10px",
                  letterSpacing: "0.3em",
                  color: "rgba(255,255,255,0.7)",
                  textShadow: "0 1px 2px rgba(0,0,0,0.25)",
                }}
              >
                STEP · 04
              </span>
              <span
                className="inline-block h-[1px] w-8"
                style={{ background: "rgba(255,255,255,0.55)" }}
              ></span>
            </div>

            {/* Bottom copy — CARD 04 */}
            <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
              <h3
                className="mb-2.5"
                style={{
                  fontFamily: "var(--font-heading)",
                  fontSize: "clamp(1.15rem, 1.4vw, 1.45rem)",
                  lineHeight: 1.2,
                  fontWeight: 500,
                  letterSpacing: "-0.01em",
                  color: "#FFFFFF",
                }}
              >
                Growing Forward
              </h3>
              <p
                className="italic mb-4"
                style={{
                  fontFamily: "var(--font-heading)",
                  fontSize: "clamp(0.88rem, 1vw, 1rem)",
                  lineHeight: 1.5,
                  fontWeight: 300,
                  color: "rgba(255,255,255,0.75)",
                }}
              >
                Small shifts settle into who you&rsquo;re becoming.
              </p>
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-500"></span>
                <span
                  className="uppercase font-medium"
                  style={{
                    fontSize: "10px",
                    letterSpacing: "0.28em",
                    color: "rgba(255,255,255,0.7)",
                  }}
                >
                  Moving onward
                </span>
              </div>
            </div>
          </article>
        </div>
      </div>
    </div>
  );
});

HowWeWorkCards.displayName = "HowWeWorkCards";

export default HowWeWorkCards;